import React from 'react';
import VoucherCard from './VoucherCard';
import Toggle from './Toggle';

// DesignCard — one tile in the design library grid.
//
// Layout: 5:3 visual area at the top rendering an actual VoucherCard
// preview, then a footer strip with optional EDIT pill (Custom only) and
// the library-availability toggle.
//
// Click behaviour:
//   - card body  → onSelect(design)  (open preview modal)
//   - toggle     → onToggle(id)      (flip available)
//   - EDIT pill  → onEdit(design)    (open custom designer)
//   - Toggle / EDIT stop propagation so the card preview doesn't open.
//
// Construction note: VoucherCard is voucher-shaped, so we synthesise a
// minimal voucher object from the design's library record. The £50 value
// is a stable placeholder — real face values come from the voucher.

const buildPreviewVoucher = (design /* salonName unused — kept by API */) => {
  if (design.isCustom) {
    return {
      name: 'Gift Card',
      template: 'signature',
      theme: design.theme,
      value: 50,
      promo: null,
      // Pass the full config through so CustomCardScene gets layout,
      // bg mode, gradient, photo focal, brand mark, etc. — anything
      // the designer saved comes through unchanged.
      customDesign: design.config,
    };
  }
  return {
    name: 'Gift Card',
    template: design.occasion || 'signature',
    theme: design.theme,
    value: 50,
    promo: null,
  };
};

const EditIcon = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 2l3 3-7 7H2v-3l7-7z" />
  </svg>
);

const DesignCard = ({
  design,
  salonName = 'Your Salon',
  currency = '£',
  onSelect,
  onToggle,
  onEdit,
}) => {
  const previewVoucher = buildPreviewVoucher(design, salonName);

  const handleCardClick = () => onSelect && onSelect(design);
  const handleCardKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onSelect) onSelect(design);
    }
  };
  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(design);
  };
  const handleToggle = (next) => {
    if (onToggle) onToggle(design.id, next);
  };

  return (
    <div
      className={`v2-design-card ${design.available ? '' : 'is-unavailable'}`}
      role="button"
      tabIndex={0}
      aria-label={`Preview ${design.label}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKey}
    >
      <div className="v2-design-card-visual">
        <VoucherCard
          voucher={previewVoucher}
          aesthetic={design.aesthetic}
          salonName={salonName}
          currency={currency}
          showPromo={false}
        />
        {design.isCustom && (
          <span className="v2-design-card-badge">Custom</span>
        )}
      </div>
      <div className="v2-design-card-footer">
        {design.isCustom ? (
          <button
            type="button"
            className="v2-edit-pill"
            onClick={handleEdit}
            aria-label={`Edit ${design.label}`}
          >
            <EditIcon /> Edit
          </button>
        ) : (
          <span className="v2-design-card-name" title={design.label}>
            {design.label.replace(/^[^·]+·\s*/, '')}
          </span>
        )}
        <Toggle
          on={design.available}
          onChange={handleToggle}
          ariaLabel={`${design.available ? 'Hide' : 'Show'} ${design.label}`}
        />
      </div>
    </div>
  );
};

export default DesignCard;
